import { useEffect, useState } from 'react'
import { useAuth } from 'context/AuthContext'
import { useFetch } from 'utils/fetch-helper'
import { FetchMethods } from 'utils/types'
import { IntegrationCards } from './Integrations/IntegrationCards'
import { useAppContext } from 'context/AppContext'
import Translation from 'components/Translation'
import Link from 'next/link'
import { useTenantContext } from 'context/TenantContext'
import { IntegrationCardProps } from './types'

function isLocationIntegration(name: string) {
    const integration_name = name.toUpperCase()

    return (
        integration_name.indexOf('PAYMENT') == 0 ||
        integration_name.indexOf('ACCOUNTING') == 0 ||
        integration_name.indexOf('BILLING') == 0
    )
}

function IntegrationsForm() {
    const { LocationModal: ctx } = useAuth()
    const { tenant, tenant_integrations, getIntegrations } = useTenantContext()
    const { translations, loadComponentTranslations } = useAppContext()
    const attributes = ctx.attributes as Record<string, string>
    const params = new URLSearchParams(location.search)
    const stripe_code = params.get('code') as unknown as string

    const [enabled, setEnabledIntegrations] = useState<
        IntegrationCardProps[]
    >([])
    const [available, setAvailableIntegrations] = useState<
    IntegrationCardProps[]
    >([])

    const { data: general_integrations, is_loading: is_fetching_genints } =
        useFetch('/v1/integrations', FetchMethods.GET)

    useEffect(() => {
        if (
            !is_fetching_genints &&
            general_integrations &&
            general_integrations.content.length > 0 &&
            available.length == 0
        ) {
            const ints = general_integrations.content.map(
                (i: Record<string, string>) => {
                    const {
                        categoryId,
                        categoryName,
                        id,
                        imageUrl,
                        name,
                        businessName,
                        businessEmail,
                    } = i
                    return {
                        business_email: businessEmail,
                        business_name: businessName,
                        category_id: categoryId,
                        category_name: categoryName,
                        name,
                        logo: imageUrl,
                        id,
                    }
                }
            )
            const payment_integrations = ints.filter(
                (int: Record<string, string>) => {
                    return isLocationIntegration(int.category_name)
                }
            )
            setAvailableIntegrations(payment_integrations)
        }
    }, [available, general_integrations, is_fetching_genints])

    useEffect(() => {
        if (tenant_integrations) {
            const ints = tenant_integrations.content.map(
                (i: Record<string, string>) => {
                    return {
                        client_secret: i.clientSecret,
                        connected_account_id: i.connectedAccountId,
                        integration_id: i.integrationId,
                        location_id: i.locationId,
                        id: i.id,
                        business_email: i.businessEmail,
                        business_name: i.businessName,
                    }
                }
            )
            setEnabledIntegrations(ints)
        }
    }, [tenant_integrations])

    useEffect(() => {
        if (!translations.INTEGRATIONS_TRANSLATION_ID) {
            loadComponentTranslations('INTEGRATIONS_TRANSLATION_ID')
        }
    }, [translations])

    useEffect(() => {
        if (tenant?.id) {
            getIntegrations().catch(console.error)
        }
    }, [attributes.to_disconnect, tenant?.id])

    useEffect(() => {
        if (tenant?.id) {
            getIntegrations().catch(console.error)
        }
    }, [attributes.activated_integration, tenant?.id])

    if (!tenant) return <>Please log in as a tenant</>

    return (
        <section className="p-10">
            {enabled.length == 0 ? (
                <div className="w-full flex gap-2 justify-between bg-primary-lighter/20 text-primary-light font-semibold rounded-lg p-4 items-center">
                    <i className="feather-info mb-[1px]" />
                    <Translation
                        content_key="locations.setup_to_enable"
                        render_as="div"
                    />
                    <div className="flex-1" />
                    <Link href="/dashboard/integrations">
                        <a className="h-10 flex justify-center items-center gap-2 rounded-lg border-slate-800/20 text-primary border px-5">
                            <Translation
                                content_key="open_integrations"
                                render_as="span"
                            />
                            <i className="feather-external-link mb-[1px]" />
                        </a>
                    </Link>
                </div>
            ) : (
                    <div className="w-full flex gap-2 justify-between bg-primary-lighter/20 text-primary-light font-semibold rounded-lg p-4 items-center">
                        <i className="feather-info mb-[1px]" />
                        <Translation
                            content_key="locations.manage_integrations"
                            render_as="div"
                        />
                        <div className="flex-1" />
                        <Link href="/dashboard/integrations">
                            <a className="h-10 flex justify-center items-center gap-2 rounded-lg border-slate-800/20 text-primary border px-5">
                                <Translation
                                    content_key="open_integrations"
                                    render_as="span"
                                />
                                <i className="feather-external-link mb-[1px]" />
                            </a>
                        </Link>
                    </div>
            )}
            <div className="flex gap-6 py-5">
                {available.length == 0 ? (
                    ''
                ) : (
                    <IntegrationCards
                        location={{ id: attributes.id }}
                        integrations={{
                            available,
                            enabled,
                        }}
                        refreshTenantIntegration={getIntegrations}
                    />
                )}
            </div>
        </section>
    )
}

export default IntegrationsForm
